import { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon, ArrowUpIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';
import { Switch } from './Switch';
import { Badge } from './Chip';

// PinnableOrderList — the DS control for a FIXED, closed set of items whose
// visibility (pin/unpin) AND order both matter (story 2-143b; first
// consumer: the dashboard's widget catalog).
//
// WHY A NEW PRIMITIVE, and why a SIBLING of `OrderedListField` rather than a
// reuse of it. `OrderedListField` (story 3-3b) owns add/remove/edit-a-row —
// its item count is open-ended and every row's CONTENT is the caller's own
// form fields. A widget catalog is the opposite shape: the item count is
// FIXED (a server-defined enum — nothing is ever added or removed, only
// shown/hidden), and each row's own content is presentational (a label/
// description this primitive renders itself), not a caller-supplied form.
// Reusing `OrderedListField` would mean carrying its "Add row"/trash-can
// affordances for a set that can never actually grow or shrink — the same
// "near-duplicate primitive" trade P18 §6 asks to avoid, just in the other
// direction. So this ships the identical MECHANICS (`OrderedListField`'s own
// a11y contract, verbatim) over the narrower "fixed set, toggle + reorder"
// shape instead.
//
// ── ACCESSIBILITY (P10 §2, D44) — identical contract to `OrderedListField` ──
//  * `<ol>`/`<li>`: position-in-set is native list semantics.
//  * Every control is a real element (`Switch`/`Button`), keyboard-operable,
//    inheriting `focus-ring`. No drag-and-drop-only affordance.
//  * Accessible names are ROW-QUALIFIED ("Pin Next up", "Move Next up up").
//  * The first row's "move up" and the last row's "move down" are DISABLED
//    rather than absent — a control that disappears mid-interaction moves
//    focus somewhere unexpected.
//  * A move is announced through a polite live region and focus follows the
//    MOVED row, onto a control that is still operable there — the exact
//    same boundary-aware focus-follow `OrderedListField#move` established.
//  * A row this principal's role may not pin (`disabled`) renders its toggle
//    disabled with a lock badge naming why — never silently omitted (a
//    missing row reads as "this doesn't exist"; a disabled one reads as
//    "this exists, but not for you" — the SAME distinction
//    `ActivationChecklistStep.reason` already draws for its own steps). This
//    is the UI's own honesty layer only — the actual enforcement is server-
//    side (see the consumer's own read/write of `allowed`); a disabled
//    switch here is a courtesy, not the authority.

export interface PinnableOrderListItem {
  /** Stable identity — the server's own enum member, never a UI-local index. */
  key: string;
  label: string;
  description?: string;
  pinned: boolean;
  /** True when this principal's role may not pin this item at all. */
  disabled?: boolean;
  /** Shown next to a disabled item — why it cannot be pinned right now. */
  disabledReason?: string;
}

export interface PinnableOrderListProps {
  id?: string;
  items: readonly PinnableOrderListItem[];
  /** The caller receives the WHOLE reordered/toggled array — same
   * whole-array convention `OrderedListField#onChange` already uses. */
  onChange: (next: PinnableOrderListItem[]) => void;
  /** Whole-list disable, e.g. while a save is in flight. */
  disabled?: boolean;
}

export function PinnableOrderList({
  id,
  items,
  onChange,
  disabled = false,
}: PinnableOrderListProps): JSX.Element {
  const baseId = id ?? 'pinnable-order-list';

  const [announcement, setAnnouncement] = useState('');
  const buttonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [focusTarget, setFocusTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!focusTarget) return;
    buttonRefs.current[focusTarget]?.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  const move = (index: number, delta: number) => {
    const target = index + delta;
    const item = items[index];
    if (target < 0 || target >= items.length || !item) return;
    const next = [...items];
    next.splice(index, 1);
    next.splice(target, 0, item);
    onChange(next);
    setAnnouncement(`${item.label} moved to position ${target + 1} of ${next.length}`);
    // Boundary-aware focus-follow — identical logic to `OrderedListField#move`.
    const forward = delta < 0 ? 'up' : 'down';
    const stillEnabled = delta < 0 ? target > 0 : target < next.length - 1;
    setFocusTarget(`${target}:${stillEnabled ? forward : forward === 'up' ? 'down' : 'up'}`);
  };

  const togglePinned = (index: number) => {
    const item = items[index];
    if (!item || item.disabled) return;
    const next = [...items];
    const nowPinned = !item.pinned;
    next[index] = { ...item, pinned: nowPinned };
    onChange(next);
    setAnnouncement(`${item.label} ${nowPinned ? 'pinned' : 'unpinned'}.`);
  };

  return (
    <div>
      <ol id={baseId} data-testid={`${baseId}-list`} className="space-y-2">
        {items.map((item, index) => {
          const rowId = `${baseId}-${index}`;
          return (
            <li key={item.key} data-testid={`${baseId}-row`} className="rounded-xl border border-border p-3">
              <div className="flex flex-wrap items-center gap-3">
                <Switch
                  value={item.pinned}
                  onChange={() => togglePinned(index)}
                  disabled={disabled || item.disabled}
                  aria-label={`${item.pinned ? 'Unpin' : 'Pin'} ${item.label}`}
                  data-testid={`${rowId}-pin`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
                  {item.disabled && (
                    <div className="mt-1">
                      <Badge tone="neutral">
                        <LockClosedIcon className="h-3.5 w-3.5" aria-hidden />
                        {item.disabledReason ?? 'Not your role'}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    ref={(node) => {
                      buttonRefs.current[`${index}:up`] = node;
                    }}
                    variant="ghost"
                    size="sm"
                    iconOnly
                    icon={<ArrowUpIcon className="h-4 w-4" />}
                    disabled={disabled || index === 0}
                    onClick={() => move(index, -1)}
                  >
                    {`Move ${item.label} up`}
                  </Button>
                  <Button
                    ref={(node) => {
                      buttonRefs.current[`${index}:down`] = node;
                    }}
                    variant="ghost"
                    size="sm"
                    iconOnly
                    icon={<ArrowDownIcon className="h-4 w-4" />}
                    disabled={disabled || index === items.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    {`Move ${item.label} down`}
                  </Button>
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* Reordering/toggling is silent to assistive tech without this. */}
      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
